export type PostSaveFlow =
  | 'lineEdited'
  | 'lineCreated'
  | 'lineDuplicated'
  | 'lineDeleted'
  | 'linePolygonChanged'
  | 'productTemplateApplied'
  | 'productTemplateCleared'
  | 'campaignEdited'
  | 'campaignAllocationFieldsChanged'
  | 'transientChangesDiscarded'
  | 'liveLineCancelled';

export type PricingSummaryRefresh = 'skip' | 'whenLinePriced' | 'always';
export type CampaignStoreUpdate = 'skip' | 'always' | 'whenSavedLineFound';
export type AvailabilityCheck = 'skip' | 'always' | 'whenSavedLineFound';
export type DealIdUpdate =
  | 'skip'
  | 'whenSaveResultDiffers'
  | 'whenFetchedCampaignIdDiffers'
  | 'whenFetchedHeaderIdDiffers';
export type SavedLineMatch = 'skip' | 'byName' | 'byId';
export type ActiveLineSelection = 'skip' | 'savedLine' | 'providedLine';

export type CampaignPostSaveRefreshSettings = {
  shouldFetchCampaign: boolean;
  updateCampaignInStore: CampaignStoreUpdate;
  pricingSummary: PricingSummaryRefresh;
  shouldSyncProductTemplates: boolean;
  checkAvailability: AvailabilityCheck;
  updateDealId: DealIdUpdate;
  shouldShowTransientBanner: boolean;
  shouldLoadPolygons: boolean;
  savedLineMatch: SavedLineMatch;
  selectActiveLine: ActiveLineSelection;
};

const defaultCampaignPostSaveRefreshSettings: CampaignPostSaveRefreshSettings = {
  shouldFetchCampaign: true,
  updateCampaignInStore: 'skip',
  pricingSummary: 'skip',
  shouldSyncProductTemplates: false,
  checkAvailability: 'skip',
  updateDealId: 'skip',
  shouldShowTransientBanner: false,
  shouldLoadPolygons: false,
  savedLineMatch: 'skip',
  selectActiveLine: 'skip',
};

const campaignPostSaveRefreshSettings: Record<PostSaveFlow, Partial<CampaignPostSaveRefreshSettings>> = {
  lineEdited: {
    pricingSummary: 'whenLinePriced',
    checkAvailability: 'always',
  },
  lineCreated: {
    updateCampaignInStore: 'whenSavedLineFound',
    updateDealId: 'whenSaveResultDiffers',
    shouldShowTransientBanner: true,
    savedLineMatch: 'byName',
    selectActiveLine: 'savedLine',
  },
  lineDuplicated: {
    updateCampaignInStore: 'whenSavedLineFound',
    checkAvailability: 'whenSavedLineFound',
    savedLineMatch: 'byId',
    selectActiveLine: 'savedLine',
  },
  lineDeleted: {
    shouldFetchCampaign: false,
    checkAvailability: 'always',
  },
  linePolygonChanged: {
    updateDealId: 'whenFetchedHeaderIdDiffers',
    shouldShowTransientBanner: true,
  },
  productTemplateApplied: {
    updateCampaignInStore: 'always',
    shouldSyncProductTemplates: true,
    checkAvailability: 'always',
    selectActiveLine: 'providedLine',
  },
  productTemplateCleared: {
    updateCampaignInStore: 'always',
    shouldSyncProductTemplates: true,
    checkAvailability: 'always',
  },
  campaignEdited: {
    updateDealId: 'whenSaveResultDiffers',
    shouldShowTransientBanner: true,
  },
  campaignAllocationFieldsChanged: {
    shouldFetchCampaign: false,
    checkAvailability: 'always',
  },
  transientChangesDiscarded: {
    updateCampaignInStore: 'always',
    pricingSummary: 'always',
    shouldSyncProductTemplates: true,
    shouldLoadPolygons: true,
  },
  liveLineCancelled: {
    updateCampaignInStore: 'always',
    pricingSummary: 'always',
    updateDealId: 'whenFetchedCampaignIdDiffers',
    selectActiveLine: 'providedLine',
  },
};

export const getCampaignPostSaveRefreshSettings = (saveFlow: PostSaveFlow): CampaignPostSaveRefreshSettings => ({
  ...defaultCampaignPostSaveRefreshSettings,
  ...campaignPostSaveRefreshSettings[saveFlow],
});
